import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  Reducer,
  SetStateAction,
  useReducer,
} from 'react';

import { LoanDatatableLoan } from 'features/drilldown/LoanDatatable/__generated__/LoanDatatableLoan';

import {
  AssetClass as GqlAssetClass,
  AssetClass,
  Filter,
  FilterableField,
  FilterOperator,
  ListingField,
  Pagination,
  Sort,
  SortableField,
  SortDirection,
} from '__generated__/globalTypes';

import { AssetClassToLoanType } from '../app-level/config/assetClassTypes';
import { FIELD_CONFIG } from '../configs/columns';
import { executedTradesColumnOrder } from '../features/deals/Executed/Table.config';
import { activeTradesAllTradesColumnOrder } from '../features/deals/InProgress/ActiveListingsTable.config';
import {
  autoColumnsOrder,
  creColumnsOrder,
  homeARMColumnsOrder,
  unsecpersonalColumnsOrder,
} from '../features/drilldown/LoanDatatable/loanTable.config';
import { listingColsOrder, ListingKey } from '../features/OnMarket/marketplace.config';
import { activeTradesBidsListingColsOrder } from '../features/pages/marketplace/ActiveTrades/bidsTable.config';
import { PageLoader } from '../ui-kit';

function transformOperand(
  name: FilterableField | ListingField,
  value: string | null | undefined,
): string | null {
  if (!value) {
    return null;
  }
  const field = Object.values(FIELD_CONFIG).find((f) => f.filterSelector === name);
  if (field && field.inputFormat && field.inputFormat.inputToSearchValue) {
    return `${field.inputFormat.inputToSearchValue(value)}`;
  }
  return value;
}

function createQueryFilters(assetClass: AssetClass | null, filters: Filter[]): Filter[] {
  return [
    {
      field_name: FilterableField.asset_class,
      operator: FilterOperator.IS,
      operand: `${assetClass}`,
    },
    ...filters.map((filter: Filter) => ({
      field_name: filter.field_name,
      operator: filter.operator,
      operand: transformOperand(filter.field_name, filter.operand),
      operandList: filter.operandList ?? null,
    })),
  ];
}

function isCursorNotEqual(
  currentCursor: DataContextPaginationCursor,
  newCursor: DataContextPaginationCursor,
): boolean {
  return (
    currentCursor == null ||
    currentCursor.loanId !== newCursor.loanId ||
    currentCursor.cursorLoanSortedValue !== newCursor.cursorLoanSortedValue
  );
}

function createPaginationParam(
  _paginationCursors: Array<DataContextPaginationCursor>,
  pagination: DataContextPagination,
): Pagination {
  return {
    page_size: pagination.pageSize,
    // cursor_id: paginationCursor.loanId,
    // cursor_sorted_value: paginationCursor.cursorLoanSortedValue,
    offset: pagination.pageSize * (pagination.currentPageNumber - 1),
  };
}

function setNewPaginationCursors(
  state: DataContextState,
  paginationCursors: Array<DataContextPaginationCursor>,
  setPaginationCursors: Dispatch<SetStateAction<DataContextPaginationCursor[]>>,
  loans?: LoanDatatableLoan[] | null,
): void {
  if (loans) {
    const lastLoan = loans[loans.length - 1];
    const sortField =
      lastLoan && state.sort.sort_field && state.sort.sort_field in lastLoan
        ? (state.sort.sort_field as keyof typeof lastLoan) // we always expect the sort field to be in the loan
        : null;
    if (sortField != null) {
      const newCursor = {
        loanId: lastLoan.id,
        cursorLoanSortedValue: String(lastLoan[sortField]),
      };
      const currentCursor = paginationCursors[state.pagination.currentPageNumber];
      if (!currentCursor) {
        paginationCursors[state.pagination.currentPageNumber] = newCursor;
      } else if (isCursorNotEqual(currentCursor, newCursor)) {
        const newPaginationCursors = paginationCursors.slice(0, state.pagination.currentPageNumber); // we remove the following pages in case the user went back
        newPaginationCursors.push(newCursor);
        setPaginationCursors(newPaginationCursors);
      }
    }
  }
}

export type DataContextPaginationCursor = {
  loanId: string | null;
  cursorLoanSortedValue: string | null;
};

const PAGINATION_CURSOR_INIT = [{ loanId: null, cursorLoanSortedValue: null }];

export type DataContextPagination = {
  currentPageNumber: number;
  pageSize: number;
};

export type marketHomeTableColumnsOrder = ListingKey | 'view-deal';

export interface GlobalTableOrderConfig {
  myPortfolio: {
    loanTable: Partial<{
      [K in keyof AssetClassToLoanType]: (keyof AssetClassToLoanType[K])[];
    }>;
  };
  marketPlace: {
    marketHomeTable: marketHomeTableColumnsOrder[];
    activeTrades: {
      allTradesTable: string[];
      bidsTable: string[];
    };
    executedTradesTable: string[];
  };
}
export const initGlobalTableOrderConfig: GlobalTableOrderConfig = {
  myPortfolio: {
    loanTable: {
      HOME: homeARMColumnsOrder,
      HOME_ARM: homeARMColumnsOrder,
      [GqlAssetClass.AUTO]: autoColumnsOrder,
      [GqlAssetClass.UNSECPERSONAL]: unsecpersonalColumnsOrder,
      [GqlAssetClass.CRE]: creColumnsOrder,
    },
  },
  marketPlace: {
    marketHomeTable: ['view-deal', ...listingColsOrder],
    activeTrades: {
      allTradesTable: activeTradesAllTradesColumnOrder,
      bidsTable: activeTradesBidsListingColsOrder,
    },
    executedTradesTable: executedTradesColumnOrder,
  },
};

export type DataContextState = {
  assetClass: AssetClass | null;
  dummy: boolean;
  isLoading: boolean;
  globalTableOrderConfig: GlobalTableOrderConfig;
  filters: Array<DataContextFilter>;
  pagination: DataContextPagination;
  sort: Sort;
};

const init: DataContextState = {
  assetClass: AssetClass.AUTO,
  globalTableOrderConfig: initGlobalTableOrderConfig,
  isLoading: false,
  dummy: false,
  filters: [],
  pagination: {
    currentPageNumber: 1,
    pageSize: 25,
  },
  sort: {
    sort_field: SortableField.account_id,
    sort_direction: SortDirection.asc,
  },
};

type DataContextType = {
  state: DataContextState;
  dispatch: Dispatch<DataContextActionTypes>;
};

const DataContext = createContext<DataContextType>({
  state: init,
  dispatch: () => {},
});

export interface DataContextFilter extends Filter {
  id: string;
}

enum DataContextActionType {
  TRIGGER_RELOAD = 'TRIGGER_RELOAD',
  SET_ASSET_CLASS = 'SET_ASSET_CLASS',
  REMOVE_ASSET_CLASS = 'REMOVE_ASSET_CLASS',
  ADD_FILTER = 'ADD_FILTER',
  SET_FILTERS = 'SET_FILTERS',
  REMOVE_FILTER = 'REMOVE_FILTER',
  REMOVE_FILTERS = 'REMOVE_FILTERS',
  REMOVE_ALL_FILTERS = 'REMOVE_ALL_FILTERS',
  SET_LOANS_SORT = 'SET_LOANS_SORT',
  SET_LOANS_CURRENT_PAGE = 'SET_LOANS_CURRENT_PAGE',
  RESET_PAGINATION = 'RESET_PAGINATION',
  SET_LOAN_PAGE_SIZE = 'SET_LOAN_PAGE_SIZE',
  SET_LOADING = 'SET_LOADING',
  SET_GLOBAL_TABLE_ORDER_CONFIG = 'SET_GLOBAL_TABLE_ORDER_CONFIG',
  INIT = 'INIT',
}

export interface ReloadAction {
  type: typeof DataContextActionType.TRIGGER_RELOAD;
}

export interface IsLoadingAction {
  type: DataContextActionType.SET_LOADING;
  payload: boolean;
}

export interface GlobalTableOrderConfigAction {
  type: DataContextActionType.SET_GLOBAL_TABLE_ORDER_CONFIG;
  payload: GlobalTableOrderConfig;
}

export interface SetAssetClassAction {
  type: typeof DataContextActionType.SET_ASSET_CLASS;
  payload: AssetClass | null;
}

export interface RemoveAssetClassAction {
  type: typeof DataContextActionType.REMOVE_ASSET_CLASS;
}

export interface AddFilterAction {
  type: typeof DataContextActionType.ADD_FILTER;
  payload: DataContextFilter;
}

export interface SetFiltersAction {
  type: typeof DataContextActionType.SET_FILTERS;
  payload: Array<DataContextFilter>;
}

export interface RemoveFilterAction {
  type: typeof DataContextActionType.REMOVE_FILTER;
  payload: string;
}

export interface RemoveFiltersAction {
  type: typeof DataContextActionType.REMOVE_FILTERS;
  payload: Array<string>;
}

export interface RemoveAllFiltersAction {
  type: typeof DataContextActionType.REMOVE_ALL_FILTERS;
}

export interface SetLoansSortAction {
  type: typeof DataContextActionType.SET_LOANS_SORT;
  payload: Sort;
}

export interface SetLoansCurrentPageAction {
  type: typeof DataContextActionType.SET_LOANS_CURRENT_PAGE;
  payload: number;
}

export interface ResetPaginationAction {
  type: typeof DataContextActionType.RESET_PAGINATION;
}

export interface SetLoanPageSizeAction {
  type: typeof DataContextActionType.SET_LOAN_PAGE_SIZE;
  payload: number;
}

export interface InitAction {
  type: typeof DataContextActionType.INIT;
}

export type DataContextActionTypes =
  | ReloadAction
  | SetAssetClassAction
  | RemoveAssetClassAction
  | AddFilterAction
  | SetFiltersAction
  | RemoveFilterAction
  | RemoveFiltersAction
  | RemoveAllFiltersAction
  | SetLoansSortAction
  | SetLoansCurrentPageAction
  | ResetPaginationAction
  | SetLoanPageSizeAction
  | IsLoadingAction
  | GlobalTableOrderConfigAction
  | InitAction;

function reducer(state: DataContextState, action: DataContextActionTypes) {
  if (process.env.NODE_ENV === 'development') {
    console.log(action);
  }
  switch (action.type) {
    case DataContextActionType.SET_GLOBAL_TABLE_ORDER_CONFIG:
      return { ...state, globalTableOrderConfig: { ...action.payload } };
    case DataContextActionType.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case DataContextActionType.TRIGGER_RELOAD:
      return { ...state, dummy: !state.dummy };
    case DataContextActionType.SET_ASSET_CLASS:
      return { ...state, assetClass: action.payload };
    case DataContextActionType.REMOVE_ASSET_CLASS:
      return { ...state, assetClass: null };
    case DataContextActionType.ADD_FILTER: {
      const stateFilters = state.filters;
      stateFilters.push(action.payload);
      return {
        ...state,
        filters: stateFilters,
      };
    }
    case DataContextActionType.SET_FILTERS: {
      return { ...state, filters: action.payload };
    }
    case DataContextActionType.REMOVE_FILTER:
      return {
        ...state,
        filters: state.filters.filter((filter) => filter.id !== action.payload),
      };
    case DataContextActionType.REMOVE_FILTERS:
      return {
        ...state,
        filters: state.filters.filter((filter) => !action.payload.includes(filter.id)),
      };
    case DataContextActionType.REMOVE_ALL_FILTERS:
      return {
        ...state,
        filters: [],
      };
    case DataContextActionType.SET_LOANS_SORT:
      return {
        ...state,
        sort: action.payload,
        pagination: {
          ...state.pagination,
          currentPageNumber: 1,
        },
      };
    case DataContextActionType.SET_LOANS_CURRENT_PAGE:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          currentPageNumber: action.payload,
        },
      };
    case DataContextActionType.RESET_PAGINATION:
      return {
        ...state,
        pagination: init.pagination,
      };
    case DataContextActionType.SET_LOAN_PAGE_SIZE:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          pageSize: action.payload,
          currentPageNumber: 1,
        },
      };
    case DataContextActionType.INIT:
      return { ...init, filters: [] };
    default:
      return { ...state };
  }
}

const DataContextProvider = (props: PropsWithChildren<Record<never, never>>): JSX.Element => {
  const { children } = props;
  const [state, dispatch] = useReducer<Reducer<DataContextState, DataContextActionTypes>>(
    reducer,
    init,
  );

  return (
    <DataContext.Provider
      value={{
        state,
        dispatch,
      }}
    >
      {state.isLoading && <PageLoader />}
      {children}
    </DataContext.Provider>
  );
};

export {
  createQueryFilters,
  DataContext,
  DataContextProvider,
  DataContextActionType,
  transformOperand,
  createPaginationParam,
  setNewPaginationCursors,
  isCursorNotEqual,
  PAGINATION_CURSOR_INIT,
};
